import { blocklistedErrorMessagePatterns } from '../../custom-ui/blocklistedErrorMessagePatterns';
import { BridgeClientError } from '../../custom-ui/bridge';
import { isUnrecoverableError } from '../../custom-ui/bridge/utils/isUnrecoverableError';
import { prepareBridgeEventAttributes } from '../../custom-ui/metrics';
import { captureAndReportError } from '../../error-reporting';
import type { TBridgeMiddleware } from '../methods/types';

/**
 * Middleware that records bridge call analytics, tracing, and Sentry error reporting for every
 * TBridgeMethod. Equivalent to the v1 `wrapFeatureWithMetricsAndAnalytics` in
 * `custom-ui/metrics.ts`, ported to the v2 TBridgeMiddleware interface.
 */
export const withMetrics: TBridgeMiddleware = (method, methodName) => async (context, payload) => {
	const {
		services: { analytics, tracing },
		extension,
		environment,
		page,
		runtime,
	} = context;
	const forgeEnvironment = extension.environmentType;
	try {
		tracing.recordBridgeCall(`${methodName}() - start`);

		// invoke the original implementation
		const result = await method(context, payload);

		tracing.recordBridgeCall(`${methodName}() - end`);
		analytics.tracker.trackBridgeSucceed({
			method: methodName,
			forgeEnvironment,
			additionalAttributes: prepareBridgeEventAttributes(methodName, { data: payload }, true),
		});
		return result;
	} catch (err) {
		const error =
			err instanceof Error ? err : new Error(`Unknown Custom UI Bridge error: ${typeof err}`);

		if (!(error instanceof BridgeClientError)) {
			if (!blocklistedErrorMessagePatterns.some((pattern) => pattern.test(error.message))) {
				analytics.tracker.trackBridgeFailed({
					method: methodName,
					errorName: error.name,
					forgeEnvironment,
					additionalAttributes: {
						...prepareBridgeEventAttributes(methodName, { data: payload }, false),
						reason: error.message,
						isUnrecoverable: isUnrecoverableError(error),
					},
				});

				void captureAndReportError({
					error,
					environment,
					errorExtensionDetails: {
						id: extension.id,
						type: extension.type,
						environmentType: extension.environmentType,
						properties: extension.properties,
						appOwnerAccountId: extension.appOwner?.accountId,
					},
					page,
					runtime,
					tracing,
				});
			}
		}
		throw err;
	}
};
